/**
 * Land multiset diff — computes what lands were added, removed, and kept
 * when comparing two snapshots of a land base.
 *
 * Pure function, no React or store dependencies. AD-068.
 */
/**
 * Compute a three-way diff between two land multisets.
 *
 * Algorithm:
 *   Union all names from before and after.
 *   For each name:
 *     kept = min(beforeQty, afterQty)
 *     added = max(0, afterQty - beforeQty)
 *     removed = max(0, beforeQty - afterQty)
 *   Sort all three output arrays alphabetically by name.
 *
 * @param before - Snapshot of lands before generation
 * @param after - Snapshot of lands after generation
 * @returns LandDiffResult with added, removed, and kept arrays
 */
export function computeLandDiff(before, after) {
    // Collect all names from both multisets
    const allNames = new Set([...before.keys(), ...after.keys()]);
    const added = [];
    const removed = [];
    const kept = [];
    for (const name of allNames) {
        const beforeQty = before.get(name) ?? 0;
        const afterQty = after.get(name) ?? 0;
        const keptQty = Math.min(beforeQty, afterQty);
        const addedQty = Math.max(0, afterQty - beforeQty);
        const removedQty = Math.max(0, beforeQty - afterQty);
        if (keptQty > 0) {
            kept.push({ name, quantity: keptQty });
        }
        if (addedQty > 0) {
            added.push({ name, quantity: addedQty });
        }
        if (removedQty > 0) {
            removed.push({ name, quantity: removedQty });
        }
    }
    // Sort alphabetically by name for determinism
    added.sort((a, b) => a.name.localeCompare(b.name));
    removed.sort((a, b) => a.name.localeCompare(b.name));
    kept.sort((a, b) => a.name.localeCompare(b.name));
    return { added, removed, kept };
}
//# sourceMappingURL=land-diff.js.map